import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { invitationsAPI } from '../lib/api';
import { toast } from 'sonner';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_rejuven-health/artifacts/rqq06olv_Rejuven%20Logo%20-%20Website.png';

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  
  const inviteToken = searchParams.get('invite');
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Invitation validation state
  const [validatingInvite, setValidatingInvite] = useState(!!inviteToken);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteError, setInviteError] = useState(null);

  useEffect(() => {
    if (inviteToken) {
      validateInvitation();
    }
  }, [inviteToken]);

  const validateInvitation = async () => {
    try {
      setValidatingInvite(true);
      const response = await invitationsAPI.validateToken(inviteToken);
      setInviteValid(true);
      setName(response.data.name || '');
      setEmail(response.data.email || '');
    } catch (error) {
      setInviteValid(false);
      setInviteError(error.response?.data?.detail || 'Invalid or expired invitation');
    } finally {
      setValidatingInvite(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!inviteToken) {
      toast.error('An invitation is required to register. Please request access first.');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await register(email, password, name, inviteToken);
      toast.success('Account created successfully! You have 14 days of free trial.');
      navigate('/onboarding');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // No invite token - show message to request access
  if (!inviteToken) {
    return (
      <div className="h-screen bg-[#FDFBF7] flex overflow-hidden">
        <div className="hidden lg:block lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#8DA399]/20 to-[#E8A095]/20" />
          <img
            src="https://images.unsplash.com/photo-1591189863430-ab87e120f312?w=800&h=1200&fit=crop"
            alt="Healthy lifestyle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#4A2C2A]/60 via-transparent to-transparent" />
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-16 xl:px-24">
          <div className="max-w-md w-full mx-auto text-center">
            <Link to="/" className="inline-block mb-8">
              <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto mx-auto" />
            </Link>

            <div className="w-16 h-16 rounded-full bg-[#E8C495]/20 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-8 h-8 text-[#E8C495]" />
            </div>

            <h1 className="font-heading text-3xl text-[#4A2C2A] mb-4">
              Invite Required
            </h1>
            <p className="text-[#4A2C2A]/70 mb-8">
              Rejuven is currently invite-only. Request access and we'll send you an invitation once approved.
            </p>

            <div className="space-y-4">
              <Link to="/?request=true">
                <Button className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl h-12">
                  Request Access
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              
              <Link to="/login">
                <Button variant="outline" className="w-full rounded-xl h-12">
                  Already have an account? Sign In
                </Button>
              </Link>
            </div>

            <p className="mt-8 text-sm text-[#4A2C2A]/50">
              Already requested access? Check your email for an invitation link.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Validating invitation
  if (validatingInvite) {
    return (
      <div className="h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#4A2C2A] animate-spin mx-auto mb-4" />
          <p className="text-[#4A2C2A]/70">Validating your invitation...</p>
        </div>
      </div>
    );
  }

  // Invalid invitation
  if (inviteError) {
    return (
      <div className="h-screen bg-[#FDFBF7] flex overflow-hidden">
        <div className="hidden lg:block lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-[#8DA399]/20 to-[#E8A095]/20" />
          <img
            src="https://images.unsplash.com/photo-1591189863430-ab87e120f312?w=800&h=1200&fit=crop"
            alt="Healthy lifestyle"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-16 xl:px-24">
          <div className="max-w-md w-full mx-auto text-center">
            <Link to="/" className="inline-block mb-8">
              <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto mx-auto" />
            </Link>

            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>

            <h1 className="font-heading text-3xl text-[#4A2C2A] mb-4">
              Invalid Invitation
            </h1>
            <p className="text-[#4A2C2A]/70 mb-8">
              {inviteError}
            </p>

            <div className="space-y-4">
              <Link to="/?request=true">
                <Button className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl h-12">
                  Request New Access
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              
              <Link to="/login">
                <Button variant="outline" className="w-full rounded-xl h-12">
                  Sign In Instead
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Valid invitation - show registration form
  return (
    <div className="h-screen bg-[#FDFBF7] flex overflow-hidden">
      {/* Left Side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-[#8DA399]/20 to-[#E8A095]/20" />
        <img
          src="https://images.unsplash.com/photo-1591189863430-ab87e120f312?w=800&h=1200&fit=crop"
          alt="Healthy lifestyle"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#4A2C2A]/60 via-transparent to-transparent" />
        <div className="absolute bottom-8 left-8 right-8">
          <h2 className="text-white text-2xl font-heading leading-relaxed mb-3">
            Welcome to Rejuven!
          </h2>
          <p className="text-white/80 text-sm">
            Your invitation has been approved. Create your account to start your 14-day free trial.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-8 lg:px-16 xl:px-24">
        <div className="max-w-md w-full mx-auto">
          <Link to="/" className="inline-block mb-6">
            <img src={LOGO_URL} alt="Rejuven" className="h-10 w-auto" />
          </Link>

          {/* Invitation Badge */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#8DA399]/10 rounded-full text-[#8DA399] text-sm font-medium mb-4">
            <ShieldCheck className="w-4 h-4" />
            Invitation Verified
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl text-[#4A2C2A] mb-2">
            Create Your Account
          </h1>
          <p className="text-[#4A2C2A]/70 mb-5 text-sm sm:text-base">
            Complete your registration to start your 14-day free trial.
          </p>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="name" className="text-[#4A2C2A] text-sm">{t('auth.name')}</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C2A]/40" />
                <Input
                  id="name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-warm pl-10 h-10"
                  required
                  data-testid="register-name-input"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="email" className="text-[#4A2C2A] text-sm">{t('auth.email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C2A]/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-warm pl-10 h-10 bg-[#4A2C2A]/5"
                  required
                  disabled
                  data-testid="register-email-input"
                />
              </div>
              <p className="text-xs text-[#4A2C2A]/50">Email is locked to your invitation</p>
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-[#4A2C2A] text-sm">{t('auth.password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C2A]/40" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-warm pl-10 pr-10 h-10"
                  required
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#4A2C2A]/40 hover:text-[#4A2C2A]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="confirmPassword" className="text-[#4A2C2A] text-sm">{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A2C2A]/40" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-warm pl-10 h-10"
                  required
                  data-testid="register-confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#4A2C2A] text-white hover:bg-[#382120] rounded-xl h-11 text-base font-medium mt-1"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Creating Account...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Start 14-Day Free Trial <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-5 text-center text-[#4A2C2A]/70 text-sm">
            {t('auth.hasAccount')}{' '}
            <Link to="/login" className="text-[#E8A095] hover:text-[#D68C82] font-medium">
              {t('auth.signIn')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
